import type { UniverseSearchModel } from '../UniverseCreationModel';
import { PresetSelectionModel } from './PresetSelectionModel';
import type { PresetSelectionModelInstance } from './PresetSelectionModel';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { computed, signal } from '@preact/signals-react';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { SelfAliasIndicatorFormula } from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import type { NumberAndMode, QuantitativeConditionOperation } from 'src/features/scranner/types';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { FormulaService } from 'src/services/FormulaService';

export interface RangeConditionInit {
    id: string;
    enabled: boolean;
    indicator: ConcreteIndicator;
    operation: QuantitativeConditionOperation;
    min: NumberAndMode | null;
    max: NumberAndMode | null;
    selection?: SelfAliasIndicatorFormula;
    searchModel: UniverseSearchModel;
    formulaService: FormulaService;
}

interface RangeConditionState {
    readonly enabled: boolean;
    readonly indicator: ConcreteIndicator;
    readonly operation: QuantitativeConditionOperation;
    readonly min: NumberAndMode | null;
    readonly max: NumberAndMode | null;
}

type RangeConditionAction =
    | { type: 'SET_ENABLED'; enabled: boolean }
    | { type: 'SET_OPERATION'; operation: QuantitativeConditionOperation }
    | { type: 'SET_MIN'; min: NumberAndMode | null }
    | { type: 'SET_MAX'; max: NumberAndMode | null };

function reducer(state: RangeConditionState, action: RangeConditionAction): RangeConditionState {
    switch (action.type) {
        case 'SET_ENABLED':
            if (state.enabled === action.enabled) return state;
            return { ...state, enabled: action.enabled };
        case 'SET_OPERATION':
            if (state.operation === action.operation) return state;
            return { ...state, operation: action.operation };
        case 'SET_MIN':
            if (state.min === action.min) return state;
            return { ...state, min: action.min };
        case 'SET_MAX':
            if (state.max === action.max) return state;
            return { ...state, max: action.max };
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Numeric range criterion: keeps symbols whose `indicator` value falls
 * within a `min`/`max` window or above/below a single bound, depending on
 * `operation` (`between`, `greaterThan`, `lessThan`).
 *
 * Use for indicators of numeric (double/int) data types. Each bound is a
 * `NumberAndMode` — the `mode` selects inclusive vs. exclusive comparison;
 * `null` means the bound is unset (only relevant for `between`). Use
 * `setMin`/`setMax` to update bounds and `setOperation` to switch mode.
 *
 * The current preset selection lives on `presetSelection`; the `selection`
 * signal exposed here is a thin computed projection used for wire
 * serialization.
 */
export class RangeConditionModel implements Disposable {
    readonly type = 'range' as const;

    readonly #state: Signal<RangeConditionState>;

    readonly id: string;
    readonly enabled: ReadonlySignal<boolean>;
    readonly indicator: ReadonlySignal<ConcreteIndicator>;
    readonly operation: ReadonlySignal<QuantitativeConditionOperation>;
    readonly min: ReadonlySignal<NumberAndMode | null>;
    readonly max: ReadonlySignal<NumberAndMode | null>;
    readonly selection: ReadonlySignal<SelfAliasIndicatorFormula | undefined>;
    readonly presetSelection: PresetSelectionModelInstance;
    readonly validationErrors: ReadonlySignal<string[]>;
    readonly isValid: ReadonlySignal<boolean>;

    constructor(init: RangeConditionInit) {
        this.id = init.id;
        this.presetSelection = new PresetSelectionModel({
            indicator: init.indicator,
            initialSelection: init.selection,
            searchModel: init.searchModel,
            formulaService: init.formulaService,
        });
        this.#state = signal<RangeConditionState>({
            enabled: init.enabled,
            indicator: init.indicator,
            operation: init.operation,
            min: init.min,
            max: init.max,
        });

        this.enabled = computed(() => this.#state.value.enabled);
        this.indicator = computed(() => this.#state.value.indicator);
        this.operation = computed(() => this.#state.value.operation);
        this.min = computed(() => this.#state.value.min);
        this.max = computed(() => this.#state.value.max);
        this.selection = computed(() => this.presetSelection.selectedFormula.value);
        this.validationErrors = computed(() => {
            const s = this.#state.value;
            const errors: string[] = [];
            if (s.operation === 'between' && s.min != null && s.max != null) {
                if (s.min.value > s.max.value) {
                    errors.push('Min must be less than or equal to max');
                } else if (s.min.value === s.max.value && (s.min.mode === 'exclusive' || s.max.mode === 'exclusive')) {
                    errors.push('Min and max cannot be equal when either bound is exclusive');
                }
            }
            return errors;
        });
        this.isValid = computed(() => this.validationErrors.value.length === 0);
    }

    #dispatch(action: RangeConditionAction): void {
        this.#state.value = reducer(this.#state.value, action);
    }

    setEnabled(enabled: boolean): void {
        this.#dispatch({ type: 'SET_ENABLED', enabled });
    }

    setOperation(operation: QuantitativeConditionOperation): void {
        this.#dispatch({ type: 'SET_OPERATION', operation });
    }

    setMin(min: NumberAndMode | null): void {
        this.#dispatch({ type: 'SET_MIN', min });
    }

    setMax(max: NumberAndMode | null): void {
        this.#dispatch({ type: 'SET_MAX', max });
    }

    [Symbol.dispose](): void {
        this.presetSelection[Symbol.dispose]();
    }
}
